<template>
  <div class="batch-detail">
    <a-page-header 
      :title="`批次：${batchDetail?.batch_code || ''}`"
      @back="goBack"
    >
      <template #extra>
        <a-button @click="fetchBatchDetail">
          <template #icon><ReloadOutlined /></template>
          刷新
        </a-button>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <!-- 基本信息 -->
      <a-card style="margin-top: 16px" v-if="batchDetail">
        <a-descriptions :column="3" :labelStyle="{ fontWeight: 'bold' }">
          <a-descriptions-item label="评估计划">
            {{ batchDetail.plan_name || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="评估目标">
            <a-tag>{{ getEvalTargetLabel(batchDetail.eval_target) }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="执行轮次">
            {{ batchDetail.test_rounds }} 轮
          </a-descriptions-item>
          <a-descriptions-item label="执行时间">
            {{ formatTime(batchDetail.start_time) }} - {{ formatTime(batchDetail.end_time) }}
          </a-descriptions-item>
          <a-descriptions-item label="耗时">
            {{ formatDuration(batchDetail.duration) }}
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="getStatusColor(batchDetail.status)">
              {{ getStatusText(batchDetail.status) }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 阶段1 & 阶段2 结果卡片 -->
      <a-row :gutter="16" style="margin-top: 16px" v-if="batchDetail">
        <!-- 阶段1 卡片 -->
        <a-col :span="12">
          <a-card title="阶段1：质量评估" :bordered="true">
            <template #extra>
              <a-tooltip title="单次执行的输出质量评估">
                <InfoCircleOutlined />
              </a-tooltip>
            </template>
            <a-row :gutter="16">
              <a-col :span="8">
                <a-statistic 
                  title="平均分" 
                  :value="batchDetail.phase1_avg_score || '-'"
                  :value-style="{ color: getAvgScoreColor(batchDetail.phase1_avg_score) }"
                />
              </a-col>
              <a-col :span="8">
                <a-statistic 
                  title="通过率" 
                  :value="batchDetail.phase1_pass_rate ? `${batchDetail.phase1_pass_rate}%` : '-'"
                />
              </a-col>
              <a-col :span="8">
                <div class="score-distribution">
                  <div class="distribution-title">分布</div>
                  <div class="distribution-content">
                    <a-tag color="success">A: {{ phase1Distribution.A }}</a-tag>
                    <a-tag color="processing">B: {{ phase1Distribution.B }}</a-tag>
                    <a-tag color="warning">C: {{ phase1Distribution.C }}</a-tag>
                  </div>
                </div>
              </a-col>
            </a-row>
          </a-card>
        </a-col>

        <!-- 阶段2 卡片 -->
        <a-col :span="12">
          <a-card title="阶段2：目标评估" :bordered="true">
            <template #extra>
              <a-tooltip title="整批执行的目标达成度评估">
                <InfoCircleOutlined />
              </a-tooltip>
            </template>
            <template v-if="batchDetail.phase2_status === 'completed'">
              <a-row :gutter="16">
                <a-col :span="8">
                  <a-statistic title="等级">
                    <template #value>
                      <a-tag 
                        :color="getScoreLevelColor(batchDetail.phase2_score_level)" 
                        style="font-size: 24px; padding: 4px 12px;"
                      >
                        {{ batchDetail.phase2_score_level }}
                      </a-tag>
                    </template>
                  </a-statistic>
                </a-col>
                <a-col :span="8">
                  <a-statistic 
                    title="分数" 
                    :value="batchDetail.phase2_score_value"
                    :value-style="{ color: getScoreLevelColorHex(batchDetail.phase2_score_level) }"
                  />
                </a-col>
                <a-col :span="8">
                  <a-statistic 
                    title="评估模板" 
                    :value="batchDetail.phase2_template_name || '-'"
                    :value-style="{ fontSize: '14px' }"
                  />
                </a-col>
              </a-row>
            </template>
            <template v-else-if="batchDetail.phase2_status === 'evaluating'">
              <a-result status="info" title="阶段2评估中...">
                <template #icon>
                  <LoadingOutlined />
                </template>
              </a-result>
            </template>
            <template v-else-if="batchDetail.phase2_status === 'failed'">
              <a-result status="error" title="阶段2评估失败" />
            </template>
            <template v-else>
              <a-result status="info" title="待评估" sub-title="批次执行完成后将自动触发阶段2评估" />
            </template>
          </a-card>
        </a-col>
      </a-row>

      <!-- 阶段2 评估理由 -->
      <a-card 
        title="阶段2评估理由" 
        style="margin-top: 16px" 
        v-if="batchDetail && batchDetail.phase2_status === 'completed' && batchDetail.phase2_score_reason"
      >
        <div class="phase2-reason">
          {{ batchDetail.phase2_score_reason }}
        </div>
        <a-collapse v-if="batchDetail.phase2_detail" style="margin-top: 16px">
          <a-collapse-panel key="1" header="详细分析">
            <pre class="detail-json">{{ JSON.stringify(batchDetail.phase2_detail, null, 2) }}</pre>
          </a-collapse-panel>
        </a-collapse>
      </a-card>

      <!-- 用例执行记录 -->
      <a-card title="用例执行记录" style="margin-top: 16px">
        <template #extra>
          <a-button 
            type="primary" 
            size="small"
            :disabled="selectedRecordKeys.length !== 2"
            @click="compareRecords"
          >
            对比选中 ({{ selectedRecordKeys.length }}/2)
          </a-button>
        </template>

        <a-table
          :columns="recordColumns"
          :data-source="records"
          :loading="recordsLoading"
          :pagination="recordsPagination"
          :row-selection="{ selectedRowKeys: selectedRecordKeys, onChange: onRecordSelectChange }"
          row-key="id"
          @change="handleRecordsTableChange"
          size="small"
        >
          <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'index'">
              {{ index + 1 }}
            </template>
            <template v-else-if="column.key === 'case_name'">
              {{ record.case_name || '-' }}
            </template>
            <template v-else-if="column.key === 'config'">
              <span>{{ record.model_name || '-' }}</span>
              <span v-if="record.round_number"> / 轮次{{ record.round_number }}</span>
            </template>
            <template v-else-if="column.key === 'score_level'">
              <a-tag 
                v-if="record.final_score_level || record.auto_score_level" 
                :color="getScoreLevelColor(record.final_score_level || record.auto_score_level)"
              >
                {{ record.final_score_level || record.auto_score_level }}
              </a-tag>
              <span v-else>-</span>
            </template>
            <template v-else-if="column.key === 'score_value'">
              {{ record.final_score_value || record.auto_score_value || '-' }}
            </template>
            <template v-else-if="column.key === 'execution_time'">
              {{ formatTime(record.execution_time) }}
            </template>
            <template v-else-if="column.key === 'action'">
              <a-button type="link" size="small" @click="viewExecutionDetail(record)">
                详情
              </a-button>
            </template>
          </template>
        </a-table>
      </a-card>
    </a-spin>

    <!-- 执行记录对比 Modal -->
    <ExecutionCompareModal
      v-model:open="compareModalVisible"
      :execution-id1="compareExecutionIds[0]"
      :execution-id2="compareExecutionIds[1]"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { ReloadOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons-vue';
import { useRouter, useRoute } from 'vue-router';
import { getPlanExecutionDetail, getPlanExecutionRecords } from '@/api/evalPlanExecutionV2';
import ExecutionCompareModal from './components/ExecutionCompareModal.vue';
import dayjs from 'dayjs';

const router = useRouter();
const route = useRoute();

const batchId = computed(() => parseInt(route.params.id));
const batchDetail = ref(null);
const records = ref([]);
const loading = ref(false);
const recordsLoading = ref(false);
const selectedRecordKeys = ref([]);
const compareModalVisible = ref(false);
const compareExecutionIds = ref([null, null]);

const recordsPagination = reactive({
  current: 1,
  pageSize: 50,
  total: 0
});

const recordColumns = [
  { title: '序号', key: 'index', width: 60 },
  { title: '评估用例', dataIndex: 'case_name', key: 'case_name', width: 180 },
  { title: '配置/轮次', key: 'config', width: 150 },
  { title: '质量等级', key: 'score_level', width: 100 },
  { title: '质量分', key: 'score_value', width: 80 },
  { title: '执行时间', key: 'execution_time', width: 150 },
  { title: '操作', key: 'action', width: 80 }
];

// 阶段1分布
const phase1Distribution = computed(() => {
  if (batchDetail.value?.phase1_score_distribution) {
    return batchDetail.value.phase1_score_distribution;
  }
  return { A: 0, B: 0, C: 0 };
});

// 获取批次详情
const fetchBatchDetail = async () => {
  loading.value = true;
  try {
    const res = await getPlanExecutionDetail(batchId.value);
    if (res.code === 200) {
      batchDetail.value = res.data;
    } else {
      message.error(res.message || '获取批次详情失败');
    }
  } catch (error) {
    message.error('获取批次详情失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

// 获取执行记录
const fetchRecords = async () => {
  recordsLoading.value = true;
  try {
    const res = await getPlanExecutionRecords(batchId.value, {
      page: recordsPagination.current,
      pageSize: recordsPagination.pageSize
    });
    if (res.code === 200) {
      records.value = res.data?.list || [];
      recordsPagination.total = res.data?.pagination?.total || 0;
    }
  } catch (error) {
    message.error('获取执行记录失败：' + error.message);
  } finally {
    recordsLoading.value = false;
  }
};

// 返回
const goBack = () => {
  router.push('/eval-v2/results');
};

// 查看执行详情
const viewExecutionDetail = (record) => {
  router.push(`/eval-v2/execution/${record.id}`);
};

// 选择记录变化
const onRecordSelectChange = (keys) => {
  if (keys.length > 2) {
    message.warning('最多选择2条记录进行对比');
    return;
  }
  selectedRecordKeys.value = keys;
};

// 对比记录
const compareRecords = () => {
  if (selectedRecordKeys.value.length !== 2) {
    message.warning('请选择2条记录进行对比');
    return;
  }
  compareExecutionIds.value = [...selectedRecordKeys.value];
  compareModalVisible.value = true;
};

// 表格变化
const handleRecordsTableChange = (pag) => {
  recordsPagination.current = pag.current;
  recordsPagination.pageSize = pag.pageSize;
  fetchRecords();
};

// 辅助函数
const getEvalTargetLabel = (target) => {
  const labels = {
    consistency: '遵从性',
    model_comparison: '模型对比',
    knowledge_structure: '知识结构',
    knowledge_content: '知识内容'
  };
  return labels[target] || target;
};

const getScoreLevelColor = (level) => {
  const colors = { A: 'success', B: 'processing', C: 'warning' };
  return colors[level] || 'default';
};

const getScoreLevelColorHex = (level) => {
  const colors = { A: '#52c41a', B: '#1890ff', C: '#faad14' };
  return colors[level] || '#999';
};

const getAvgScoreColor = (score) => {
  if (!score) return '#999';
  if (score >= 90) return '#52c41a';
  if (score >= 70) return '#1890ff';
  return '#faad14';
};

const getStatusColor = (status) => {
  const colors = {
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'default'
  };
  return colors[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    running: '执行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已取消'
  };
  return texts[status] || status;
};

const formatTime = (time) => {
  if (!time) return '-';
  return dayjs(time).format('MM-DD HH:mm:ss');
};

const formatDuration = (ms) => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainSeconds = seconds % 60;
  if (minutes > 0) {
    return `${minutes}分${remainSeconds}秒`;
  }
  return `${remainSeconds}秒`;
};

onMounted(() => {
  fetchBatchDetail();
  fetchRecords();
});
</script>

<style scoped>
.batch-detail {
  padding: 24px;
}

.score-distribution {
  text-align: center;
}

.distribution-title {
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
  margin-bottom: 8px;
}

.distribution-content {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.phase2-reason {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
  line-height: 1.8;
  white-space: pre-wrap;
}

.detail-json {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  max-height: 400px;
  overflow: auto;
  font-size: 12px;
}
</style>

